import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { usePageLanguage } from '@/hooks/usePageLanguage';
import { useJsonLd } from '@/hooks/useJsonLd';
import { SITE_ORIGIN } from '@/data/site';
import { MAIN_SITE_URL } from '@/data/categories';

const PUBLISHED_DATE = '2026-08-19';
const UPDATED_DATE = '2026-08-19';

const TABLE_ROWS = [
  { aspect: 'article.tbl.r1a', spread: 'article.tbl.r1s', trad: 'article.tbl.r1t' },
  { aspect: 'article.tbl.r2a', spread: 'article.tbl.r2s', trad: 'article.tbl.r2t' },
  { aspect: 'article.tbl.r3a', spread: 'article.tbl.r3s', trad: 'article.tbl.r3t' },
  { aspect: 'article.tbl.r4a', spread: 'article.tbl.r4s', trad: 'article.tbl.r4t' },
  { aspect: 'article.tbl.r5a', spread: 'article.tbl.r5s', trad: 'article.tbl.r5t' },
];

const STEPS = [
  { titleKey: 'article.step1Title', descKey: 'article.step1Desc' },
  { titleKey: 'article.step2Title', descKey: 'article.step2Desc' },
  { titleKey: 'article.step3Title', descKey: 'article.step3Desc' },
  { titleKey: 'article.step4Title', descKey: 'article.step4Desc' },
];

const BULLETS = ['article.bullet1', 'article.bullet2', 'article.bullet3', 'article.bullet4'];
const TIPS = ['article.tip1', 'article.tip2', 'article.tip3', 'article.tip4'];
const FAQS = [
  { qKey: 'article.faq1q', aKey: 'article.faq1a' },
  { qKey: 'article.faq2q', aKey: 'article.faq2a' },
  { qKey: 'article.faq3q', aKey: 'article.faq3a' },
  { qKey: 'article.faq4q', aKey: 'article.faq4a' },
];

export default function Article() {
  const { lang = 'en' } = useParams();
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  usePageLanguage(lang);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useJsonLd('article-schema', [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: t('article.h1'),
      description: t('article.metaDescription'),
      author: { '@type': 'Organization', name: t('article.author') },
      datePublished: PUBLISHED_DATE,
      dateModified: UPDATED_DATE,
      inLanguage: lang,
      mainEntityOfPage: `${SITE_ORIGIN}/${lang}/basetao-spreadsheet-guide`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: t('logo.name'), item: `${SITE_ORIGIN}/${lang}` },
        { '@type': 'ListItem', position: 2, name: t('article.h1'), item: `${SITE_ORIGIN}/${lang}/basetao-spreadsheet-guide` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((item) => ({
        '@type': 'Question',
        name: t(item.qKey),
        acceptedAnswer: { '@type': 'Answer', text: t(item.aKey) },
      })),
    },
  ]);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main className="pt-16">
        <article className="mx-auto max-w-3xl px-4 md:px-6 py-10 md:py-16">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-xs text-foreground-500">
              <li>
                <Link to={`/${lang}`} className="hover:text-primary-600 transition-colors cursor-pointer">
                  {t('logo.name')}
                </Link>
              </li>
              <li className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-sm" />
              </li>
              <li className="text-foreground-700 font-medium">{t('article.h1')}</li>
            </ol>
          </nav>

          <header className="mb-8">
            <p className="text-xs font-semibold tracking-[0.18em] uppercase text-primary-600 mb-3">
              {t('article.eyebrow')}
            </p>
            <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-foreground-950 leading-tight mb-4">
              {t('article.h1')}
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line text-sm" />
                </span>
                {t('article.author')}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line text-sm" />
                </span>
                {t('article.published')} {PUBLISHED_DATE}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-refresh-line text-sm" />
                </span>
                {t('article.updated')} {UPDATED_DATE}
              </span>
            </div>
          </header>

          <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-8">
            {t('article.intro')}
          </p>

          {/* Table of contents */}
          <div className="rounded-lg border border-background-200 bg-background-100 p-5 mb-10">
            <p className="font-heading font-semibold text-sm text-foreground-900 mb-3">
              {t('article.tocTitle')}
            </p>
            <ul className="space-y-2">
              {['s1Title', 's2Title', 's3Title', 's4Title', 's5Title', 's6Title', 's7Title'].map(
                (k, i) => (
                  <li key={k}>
                    <button
                      type="button"
                      onClick={() => scrollTo(`section-${i + 1}`)}
                      className="text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer text-left"
                    >
                      {t(`article.${k}`)}
                    </button>
                  </li>
                ),
              )}
            </ul>
          </div>

          {/* Section 1 */}
          <section id="section-1" className="mb-10">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
              {t('article.s1Title')}
            </h2>
            <p className="text-foreground-700 leading-relaxed mb-4">{t('article.s1a')}</p>
            <p className="text-foreground-700 leading-relaxed">{t('article.s1b')}</p>
          </section>

          {/* Section 2 */}
          <section id="section-2" className="mb-10">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
              {t('article.s2Title')}
            </h2>
            <p className="text-foreground-700 leading-relaxed mb-4">{t('article.s2a')}</p>
            <ul className="space-y-3">
              {BULLETS.map((key) => (
                <li key={key} className="flex items-start gap-3 text-foreground-700">
                  <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0 mt-0.5">
                    <i className="ri-check-line text-xs" />
                  </span>
                  <span className="leading-relaxed">{t(key)}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Section 3 */}
          <section id="section-3" className="mb-10">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
              {t('article.s3Title')}
            </h2>
            <p className="text-foreground-700 leading-relaxed mb-5">{t('article.s3a')}</p>
            <div className="space-y-4">
              {STEPS.map((step, i) => (
                <div key={step.titleKey} className="flex gap-4">
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-heading font-bold text-sm shrink-0">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-heading font-semibold text-base text-foreground-900 mb-1">
                      {t(step.titleKey)}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{t(step.descKey)}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 4 — table */}
          <section id="section-4" className="mb-10">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
              {t('article.s4Title')}
            </h2>
            <p className="text-foreground-700 leading-relaxed mb-5">{t('article.s4a')}</p>
            <div className="overflow-x-auto rounded-lg border border-background-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-primary-500 text-background-50">
                    <th className="text-left px-4 py-3 font-semibold">{t('article.tbl.aspect')}</th>
                    <th className="text-left px-4 py-3 font-semibold">{t('article.tbl.spread')}</th>
                    <th className="text-left px-4 py-3 font-semibold">{t('article.tbl.trad')}</th>
                  </tr>
                </thead>
                <tbody>
                  {TABLE_ROWS.map((row, i) => (
                    <tr
                      key={row.aspect}
                      className={i % 2 === 0 ? 'bg-background-50' : 'bg-background-100'}
                    >
                      <td className="px-4 py-3 font-medium text-foreground-900">{t(row.aspect)}</td>
                      <td className="px-4 py-3 text-foreground-700">{t(row.spread)}</td>
                      <td className="px-4 py-3 text-foreground-600">{t(row.trad)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 5 */}
          <section id="section-5" className="mb-10">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
              {t('article.s5Title')}
            </h2>
            <p className="text-foreground-700 leading-relaxed">{t('article.s5a')}</p>
          </section>

          {/* Section 6 — tips */}
          <section id="section-6" className="mb-10">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
              {t('article.s6Title')}
            </h2>
            <div className="rounded-lg border border-background-200 bg-background-100 p-5 space-y-3">
              {TIPS.map((key) => (
                <div key={key} className="flex items-start gap-3">
                  <span className="w-5 h-5 flex items-center justify-center text-accent-600 shrink-0 mt-0.5">
                    <i className="ri-lightbulb-line text-base" />
                  </span>
                  <p className="text-sm text-foreground-700 leading-relaxed">{t(key)}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Section 7 — conclusion */}
          <section id="section-7" className="mb-10">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
              {t('article.s7Title')}
            </h2>
            <p className="text-foreground-700 leading-relaxed">{t('article.s7a')}</p>
          </section>

          {/* CTA */}
          <div className="rounded-lg bg-primary-50 border border-primary-200 p-8 text-center mb-10">
            <h2 className="font-heading font-bold text-xl text-foreground-950 mb-2">
              {t('article.ctaTitle')}
            </h2>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 text-background-50 px-6 py-3 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap mt-4"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-external-link-line text-base" />
              </span>
              {t('article.ctaButton')}
            </a>
          </div>

          {/* FAQ */}
          <section className="mb-10">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-5">
              {t('article.faqTitle')}
            </h2>
            <div className="space-y-3">
              {FAQS.map((item, i) => {
                const isOpen = openFaq === i;
                return (
                  <div
                    key={item.qKey}
                    className="rounded-lg border border-background-200 bg-background-50 overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                      aria-expanded={isOpen}
                    >
                      <span className="font-heading font-semibold text-sm md:text-base text-foreground-900">
                        {t(item.qKey)}
                      </span>
                      <span className="w-6 h-6 flex items-center justify-center shrink-0 text-primary-600">
                        <i
                          className={`text-lg transition-transform ${isOpen ? 'ri-subtract-line' : 'ri-add-line'}`}
                        />
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5">
                        <p className="text-sm text-foreground-600 leading-relaxed">{t(item.aKey)}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Internal link back home */}
          <div className="border-t border-background-200 pt-6">
            <Link
              to={`/${lang}`}
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line text-base" />
              </span>
              {t('article.backHome')}
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}